/**
 * Shared KYC FATCA field helpers / schema
 */
import {
  OCCUPATION_OPTIONS,
  INCOME_SLAB_OPTIONS
} from 'src/constants/kyc-profile.constants'

export const KYC_FATCA_DEFAULT_VALUES = {
  birthPlace: '',
  occupation: '',
  incomeSlab: ''
}

export const KYC_FATCA_INPUT_STYLES = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#2A2D3A',
    height: '56px',
    '& fieldset': {
      borderColor: '#3F4252'
    },
    '&:hover fieldset': {
      borderColor: '#1455FF'
    },
    '&.Mui-focused fieldset': {
      borderColor: '#1455FF'
    }
  },
  '& .MuiInputLabel-root': {
    color: 'white',
    fontSize: '14px',
    fontWeight: 400
  },
  '& .MuiInputLabel-asterisk': {
    color: 'red'
  },
  '& .MuiInputBase-input': {
    color: 'white',
    fontSize: '14px'
  },
  '& .MuiInputBase-input::placeholder': {
    color: '#808080',
    opacity: 1
  },
  '& .MuiSelect-icon': {
    color: '#C7D1E8'
  }
}

/**
 * react-hook-form `rules` for the editable FATCA fields.
 * Matches the project pattern (Controller rules) instead of a resolver schema.
 */
export const KYC_FATCA_RULES = {
  birthPlace: {
    required: 'Place of birth is required',
    validate: (value) => {
      const trimmed = typeof value === 'string' ? (value?.trim?.() ?? '') : ''
      if (!trimmed) return 'Place of birth is required'
      if ((trimmed?.length ?? 0) < 2) {
        return 'Place of birth must be at least 2 characters'
      }
      return true
    }
  },
  occupation: {
    required: 'Occupation is required',
    validate: (value) =>
      (String(value ?? '')?.trim?.()?.length ?? 0) > 0 ||
      'Please select a valid occupation'
  },
  incomeSlab: {
    required: 'Annual income is required',
    validate: (value) =>
      !isNullIncomeSlabValue?.(value) || 'Please select a valid income slab'
  }
}

const OCCUPATION_ALIASES = {
  public: 'Public',
  'public service': 'Public',
  private: 'Private',
  'private service': 'Private',
  business: 'Business',
  student: 'Student',
  housewife: 'Housewife',
  agriculture: 'Agriculture',
  retired: 'Retired',
  others: 'Others',
  other: 'Others'
}

const INCOME_ALIASES = {
  'below-1l': 'Below-1L',
  'below 1l': 'Below-1L',
  'below_1_lakh': 'Below-1L',
  'below 1 lakh': 'Below-1L',
  'below 1 lac': 'Below-1L',
  'below 1 lacs': 'Below-1L',
  'below-1lacs': 'Below-1L',
  'below1lacs': 'Below-1L',
  '1-5l': '1-5L',
  '1_to_5_lakhs': '1-5L',
  '1 to 5 lakhs': '1-5L',
  '1-5 lakhs': '1-5L',
  '1-5 lacs': '1-5L',
  '1-5lacs': '1-5L',
  '5-10l': '5-10L',
  '5_to_10_lakhs': '5-10L',
  '5 to 10 lakhs': '5-10L',
  '5-10 lakhs': '5-10L',
  '5-10 lacs': '5-10L',
  '5-10lacs': '5-10L',
  '10-25l': '10-25L',
  '10_to_25_lakhs': '10-25L',
  '10 to 25 lakhs': '10-25L',
  '10-25 lakhs': '10-25L',
  '10-25 lacs': '10-25L',
  '10-25lacs': '10-25L',
  '25-1cr': '25-1Cr',
  '25l-1cr': '25-1Cr',
  '25_lakhs_to_1_crore': '25-1Cr',
  '25 lakhs to 1 crore': '25-1Cr',
  '25-1 crore': '25-1Cr',
  '25 lacs to 1 crore': '25-1Cr',
  '25-1cr lacs': '25-1Cr',
  'above-1cr': 'Above-1Cr',
  'above 1cr': 'Above-1Cr',
  'above_1_crore': 'Above-1Cr',
  'above 1 crore': 'Above-1Cr',
  'above 1 cr': 'Above-1Cr',
  'more than 1 crore': 'Above-1Cr'
}

const toComparableKey = (value) => {
  if (value == null) return ''
  return String(value)
    ?.trim?.()
    ?.toLowerCase?.()
    ?.replace?.(/\s+/g, ' ')
    ?? ''
}

/**
 * Tokens the API sends when the income slab is not actually set.
 * BE returns "00" (a zero-only code) instead of null in that case.
 */
const NULLISH_INCOME_TOKENS = ['null', 'undefined', 'na', 'n/a', 'nil', '-', '--']

/**
 * True when the income slab coming from BE means "not provided".
 * Covers empty values and zero-only codes such as "0", "00", "000".
 */
export const isNullIncomeSlabValue = (value) => {
  if (value == null) return true
  const trimmed = String(value)?.trim?.() ?? ''
  if (!trimmed) return true
  if (/^0+$/?.test?.(trimmed)) return true
  return NULLISH_INCOME_TOKENS?.includes?.(trimmed?.toLowerCase?.() ?? '') ?? false
}

/**
 * Blank out placeholder income slabs so the dropdown stays empty and the
 * user has to pick a real slab before submitting.
 */
export const sanitizeIncomeSlabValue = (value) =>
  (isNullIncomeSlabValue?.(value) ? '' : value)

/**
 * Normalize occupation from profile/KRA to API option value
 */
export const normalizeOccupation = (value) => {
  if (value == null || value === '') return ''
  const trimmed = String(value)?.trim?.() ?? ''
  if (!trimmed) return ''

  const lower = toComparableKey(trimmed)

  // Alias map (handles Private Service, private, etc.)
  if (OCCUPATION_ALIASES?.[lower]) {
    return OCCUPATION_ALIASES?.[lower] ?? ''
  }

  const exact = OCCUPATION_OPTIONS?.find?.(
    (opt) =>
      opt?.value?.toLowerCase?.() === lower ||
      opt?.label?.toLowerCase?.() === lower
  )
  if (exact?.value) return exact?.value ?? ''

  // Prefix / includes match e.g. "Private Service" → Private
  const byIncludes = OCCUPATION_OPTIONS?.find?.(
    (opt) => {
      const optKey = opt?.value?.toLowerCase?.() ?? ''
      return optKey && (lower?.startsWith?.(optKey) || lower?.includes?.(optKey))
    }
  )
  return byIncludes?.value ?? ''
}

/**
 * Strip common income unit suffixes (lacs/lakhs/lac/lakh/L) for option matching.
 * e.g. "10-25 Lacs" → "10-25", "10-25L" → "10-25"
 */
const stripIncomeUnitSuffix = (value) => {
  if (value == null || value === '') return ''
  return String(value)
    ?.toLowerCase?.()
    ?.replace?.(/\b(lacs?|lakhs?)\b/g, '')
    ?.replace?.(/(lacs?|lakhs?)$/g, '')
    ?.replace?.(/-?l$/i, '')
    ?.replace?.(/[\s_]+/g, '')
    ?? ''
}

/**
 * Normalize income slab from profile/KRA to API option value
 */
export const normalizeIncomeSlab = (value) => {
  // "00" (and other zero-only codes) from BE means no income slab on record
  if (isNullIncomeSlabValue?.(value)) return ''
  const trimmed = String(value)?.trim?.() ?? ''
  if (!trimmed) return ''

  const lower = toComparableKey(trimmed) ?? ''
  const compact = lower?.replace?.(/[\s_]/g, '') ?? ''
  const withoutUnits = stripIncomeUnitSuffix(lower) ?? ''

  if (INCOME_ALIASES?.[lower]) {
    return INCOME_ALIASES?.[lower] ?? ''
  }
  if (INCOME_ALIASES?.[compact]) {
    return INCOME_ALIASES?.[compact] ?? ''
  }

  const exact = INCOME_SLAB_OPTIONS?.find?.(
    (opt) => {
      const optValue = opt?.value?.toLowerCase?.() ?? ''
      const optLabel = opt?.label?.toLowerCase?.() ?? ''
      const optValueCompact = optValue?.replace?.(/[\s_]/g, '') ?? ''
      const optLabelCompact = optLabel?.replace?.(/[\s_]/g, '') ?? ''
      const optValueWithoutUnits = stripIncomeUnitSuffix(optValue) ?? ''
      const optLabelWithoutUnits = stripIncomeUnitSuffix(optLabel) ?? ''
      return (
        optValue === lower ||
        optLabel === lower ||
        optValueCompact === compact ||
        optLabelCompact === compact ||
        (!!withoutUnits && (
          optValueWithoutUnits === withoutUnits ||
          optLabelWithoutUnits === withoutUnits ||
          optValueCompact === withoutUnits ||
          optLabelCompact === withoutUnits
        ))
      )
    }
  )
  return exact?.value ?? ''
}

/**
 * Pick first non-empty string from candidate values (supports number coercion)
 */
const pickFirstValue = (...candidates) => {
  for (let i = 0; i < (candidates?.length ?? 0); i += 1) {
    const candidate = candidates?.[i]
    if (candidate == null) continue
    if (typeof candidate === 'string' && candidate?.trim?.() !== '') {
      return candidate?.trim?.() ?? ''
    }
    if (typeof candidate === 'number' && !Number.isNaN(candidate)) {
      return String(candidate) ?? ''
    }
  }
  return ''
}

/**
 * Build form default values from profile / KRA prefill object
 */
export const buildKycFatcaFormDefaults = (defaultValues = null) => {
  if (!defaultValues) {
    return { ...KYC_FATCA_DEFAULT_VALUES }
  }

  const birthPlaceRaw = pickFirstValue(
    defaultValues?.birthPlace,
    defaultValues?.placeOfBirth,
    defaultValues?.place_of_birth,
    defaultValues?.birth_place
  )
  const occupationRaw = pickFirstValue(
    defaultValues?.occupation,
    defaultValues?.occupationType,
    defaultValues?.Occupation
  )
  // Strip placeholder values ("00") first so a real slab in a later key still wins
  const incomeRaw = pickFirstValue(
    sanitizeIncomeSlabValue?.(defaultValues?.incomeSlab),
    sanitizeIncomeSlabValue?.(defaultValues?.income_slab),
    sanitizeIncomeSlabValue?.(defaultValues?.income),
    sanitizeIncomeSlabValue?.(defaultValues?.annualIncome),
    sanitizeIncomeSlabValue?.(defaultValues?.annual_income),
    sanitizeIncomeSlabValue?.(defaultValues?.grossIncome),
    sanitizeIncomeSlabValue?.(defaultValues?.gross_income)
  )

  // Prefer mapped option codes; fall back to raw API text so the select can show it as-is
  const occupationNormalized = normalizeOccupation?.(occupationRaw) ?? ''
  const incomeNormalized = normalizeIncomeSlab?.(incomeRaw) ?? ''

  return {
    birthPlace: birthPlaceRaw ?? '',
    occupation: occupationNormalized || occupationRaw || '',
    incomeSlab: incomeNormalized || incomeRaw || ''
  }
}

/**
 * Ensure current form/API value appears in select options (value + label = raw string).
 * Avoids empty MUI Select when API returns a display string not in the option list.
 */
export const withCurrentSelectOption = (options = [], currentValue = '') => {
  const base = Array.isArray(options) ? options : []
  const current =
    typeof currentValue === 'string' ? (currentValue?.trim?.() ?? '') : ''
  if (!current) return base ?? []

  const exists = base?.some?.(
    (opt) =>
      opt?.value === current ||
      opt?.label === current ||
      String(opt?.value ?? '')?.toLowerCase?.() === current?.toLowerCase?.() ||
      String(opt?.label ?? '')?.toLowerCase?.() === current?.toLowerCase?.()
  )
  if (exists) return base ?? []

  return [{ value: current, label: current }, ...(base ?? [])]
}
